/**
 * MarketSnapshotsInput Component
 *
 * Content-only component for displaying and reordering market state snapshots
 * during the pre-trading ritual. Shows list of instrument/timeframe entries
 * with completion indicators and drag-and-drop reordering.
 *
 * This component renders just the list content (no Card wrapper) - the parent
 * component (PreTradingPhase) is responsible for providing the Card chrome.
 *
 * Phase 1: List display, completion dots, reordering
 * Phase 3: Add/Edit/Delete modal functionality
 */

// 1. External dependencies
import React, { useCallback, useMemo, useState } from 'react';

// 2. Internal absolute imports
import {
  Card,
  CardBody,
  Button,
  EmptyState,
  SortableList,
  DragHandle,
  Tooltip,
} from '@enfomentis/ui';
import type { DragHandleProps } from '@enfomentis/ui';
import { LineChart, Plus, Pencil, Trash2 } from 'lucide-react';
import type { MarketStateDraft } from '@/types/transient/MarketStateDraft';

// 3. Relative imports
import { MarketSnapshotEditModal } from './MarketSnapshotEditModal';
import { DeleteSnapshotConfirmationModal } from './DeleteSnapshotConfirmationModal';

// =============================================================================
// Types
// =============================================================================

/**
 * Props for MarketSnapshotsInput component
 */
export interface MarketSnapshotsInputProps {
  /** Current market states array */
  marketStates: MarketStateDraft[];
  /** Callback when market states change (reorder, add, edit, delete) */
  onMarketStatesChange: (marketStates: MarketStateDraft[]) => void;
}

/**
 * Adapter type for SortableList (requires `id` property)
 * Uses MarketStateDraft.id as the sortable ID
 */
interface SortableMarketStateItem {
  /** Unique ID for SortableList (same as MarketStateDraft.id) */
  id: string;
  /** The full MarketStateDraft */
  draft: MarketStateDraft;
}

/**
 * Props for SnapshotRowRenderer
 */
interface SnapshotRowRendererProps {
  item: SortableMarketStateItem;
  dragHandleProps: DragHandleProps;
  onEdit: (draftId: string) => void;
  onDelete: (draftId: string) => void;
}

/**
 * Completion status for a market state entry
 */
type CompletionStatus = 'empty' | 'partial' | 'complete';

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Check if a field value is considered "filled" (non-empty)
 * Empty = null, undefined, '' (empty string)
 * Valid = 0, false, non-empty strings, numbers, ImageReference objects
 */
function isFieldValueFilled(value: unknown): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === 'string' && value === '') {
    return false;
  }
  // 0 and false are valid filled values
  // Objects (ImageReference) are considered filled
  return true;
}

/**
 * Count filled fields by iterating over configured fields and checking values
 */
function countFilledFields(draft: MarketStateDraft): number {
  const preSnapshot = draft.snapshots.find(s => s.type === 'pre');
  if (!preSnapshot) {
    return 0;
  }

  let filledCount = 0;
  for (const field of draft.fieldConfigSnapshot) {
    const value = preSnapshot.data[field.fieldId];
    if (isFieldValueFilled(value)) {
      filledCount++;
    }
  }
  return filledCount;
}

/**
 * Calculate completion status based on pre-snapshot data
 */
function getCompletionStatus(draft: MarketStateDraft): CompletionStatus {
  const totalFields = draft.fieldConfigSnapshot.length;

  // If no fields configured, treat as empty
  if (totalFields === 0) {
    return 'empty';
  }

  // Count only non-empty values for configured fields
  const filledCount = countFilledFields(draft);

  if (filledCount === 0) {
    return 'empty';
  } else if (filledCount >= totalFields) {
    return 'complete';
  } else {
    return 'partial';
  }
}

/**
 * Get display color class for completion dot
 */
function getCompletionDotClass(status: CompletionStatus): string {
  switch (status) {
    case 'complete':
      return 'bg-success-500';
    case 'partial':
      return 'bg-success-300';
    case 'empty':
    default:
      return 'bg-default-300';
  }
}

/**
 * Get tooltip text for completion status
 */
function getCompletionTooltip(status: CompletionStatus, draft: MarketStateDraft): string {
  const totalFields = draft.fieldConfigSnapshot.length;

  if (totalFields === 0) {
    return 'No fields configured';
  }

  // Use consistent counting logic
  const filledCount = countFilledFields(draft);

  switch (status) {
    case 'complete':
      return `All ${totalFields} fields complete`;
    case 'partial':
      return `${filledCount} of ${totalFields} fields complete`;
    case 'empty':
    default:
      return 'No fields filled';
  }
}

/**
 * Format a field value for display in pinToLive preview
 * Resolves dropdown option ids to labels
 */
function formatFieldValue(
  value: string | number | boolean | null | object,
  fieldConfig: MarketStateDraft['fieldConfigSnapshot'][number]
): string {
  if (value === null || value === undefined) {
    return '';
  }

  // Handle dropdown - resolve option id to label
  if (fieldConfig.type === 'dropdown' && typeof value === 'string') {
    const option = fieldConfig.options?.find(o => o.id === value);
    return option?.label ?? value;
  }

  // Handle checkbox
  if (fieldConfig.type === 'checkbox') {
    return value ? 'Yes' : 'No';
  }

  // Handle image (just show indicator)
  if (fieldConfig.type === 'image' && typeof value === 'object') {
    return '[Image]';
  }

  // Default: convert to string
  return String(value);
}

/**
 * Preview item for pinToLive fields (local type, not exported)
 */
interface PinToLivePreviewItem {
  /** Stable key for React rendering */
  fieldId: string;
  /** Display name */
  name: string;
  /** Formatted value */
  value: string;
}

/**
 * Get pinToLive field previews for display
 */
function getPinToLivePreview(draft: MarketStateDraft): PinToLivePreviewItem[] {
  const preSnapshot = draft.snapshots.find(s => s.type === 'pre');
  if (!preSnapshot) {
    return [];
  }

  const previews: PinToLivePreviewItem[] = [];

  for (const field of draft.fieldConfigSnapshot) {
    if (!field.pinToLive) {
      continue;
    }

    const rawValue = preSnapshot.data[field.fieldId];
    if (rawValue === undefined || rawValue === null) {
      continue;
    }

    const formattedValue = formatFieldValue(rawValue, field);
    if (formattedValue) {
      previews.push({ fieldId: field.fieldId, name: field.name, value: formattedValue });
    }
  }

  return previews;
}

// =============================================================================
// Sub-Components
// =============================================================================

/**
 * Renders a filled market state row with drag handle, info, and action buttons
 */
const SnapshotRowRenderer: React.FC<SnapshotRowRendererProps> = ({
  item,
  dragHandleProps,
  onEdit,
  onDelete,
}) => {
  const { draft } = item;

  // Derive display values
  const instrumentName = draft.instrumentSnapshot?.displayName
    ?? draft.instrumentSnapshot?.symbol
    ?? 'Select instrument...';
  const hasInstrument = !!draft.instrumentSnapshot;
  const timeframe = draft.timeframe;

  // Completion status
  const completionStatus = getCompletionStatus(draft);
  const dotClass = getCompletionDotClass(completionStatus);
  const tooltipText = getCompletionTooltip(completionStatus, draft);

  // pinToLive previews
  const previews = getPinToLivePreview(draft);

  return (
    <Card shadow="sm" radius="md">
      <CardBody className="flex flex-col gap-2 py-3 px-3">
        {/* Row 1: Drag handle + Info + Actions */}
        <div className="flex flex-row items-center gap-2">
          <DragHandle dragHandleProps={dragHandleProps} className="flex-shrink-0" />

          {/* Instrument / Timeframe display */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`truncate ${hasInstrument ? 'text-text-primary' : 'text-text-secondary italic'}`}>
                {instrumentName}
              </span>
              {hasInstrument && (
                <>
                  <span className="text-text-tertiary">/</span>
                  <span className="text-text-secondary">{timeframe}</span>
                </>
              )}

              {/* Completion dot */}
              <Tooltip content={tooltipText}>
                <div
                  className={`w-3 h-3 rounded-full flex-shrink-0 ${dotClass}`}
                  aria-label={tooltipText}
                />
              </Tooltip>
            </div>
          </div>

          {/* Edit button */}
          <Tooltip content="Edit snapshot">
            <Button
              isIconOnly
              size="sm"
              variant="light"
              onPress={() => onEdit(draft.id)}
              aria-label="Edit snapshot"
            >
              <Pencil size={16} />
            </Button>
          </Tooltip>

          {/* Delete button */}
          <Tooltip content="Delete snapshot">
            <Button
              isIconOnly
              size="sm"
              variant="light"
              color="danger"
              onPress={() => onDelete(draft.id)}
              aria-label="Delete snapshot"
            >
              <Trash2 size={16} />
            </Button>
          </Tooltip>
        </div>

        {/* Row 2: pinToLive previews (if any) */}
        {previews.length > 0 && (
          <div className="pl-8 flex flex-wrap gap-x-4 gap-y-1 text-sm text-text-secondary">
            {previews.map((preview) => (
              <span key={preview.fieldId}>
                <span className="text-text-tertiary">{preview.name}:</span>{' '}
                <span>{preview.value}</span>
              </span>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
};

// =============================================================================
// Main Component
// =============================================================================

/**
 * MarketSnapshotsInput Component
 *
 * Renders list of market state snapshots with drag-and-drop reordering.
 * Shows completion status and pinToLive field previews for each entry.
 */
export const MarketSnapshotsInput: React.FC<MarketSnapshotsInputProps> = ({
  marketStates,
  onMarketStatesChange,
}) => {
  // =============================================================================
  // Modal State
  // =============================================================================

  // Edit modal state: null = closed, undefined = add mode, MarketStateDraft = edit mode
  const [editingDraft, setEditingDraft] = useState<MarketStateDraft | undefined>(undefined);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Delete confirmation modal state
  const [deletingDraft, setDeletingDraft] = useState<MarketStateDraft | null>(null);

  // =============================================================================
  // Derived Data
  // =============================================================================

  // Transform to SortableList format
  const sortableItems: SortableMarketStateItem[] = useMemo(
    () => marketStates.map((draft) => ({
      id: draft.id,
      draft,
    })),
    [marketStates]
  );

  // =============================================================================
  // Handlers
  // =============================================================================

  // Handle reorder from SortableList
  const handleReorder = useCallback(
    (reorderedItems: SortableMarketStateItem[]) => {
      const now = new Date().toISOString();
      const reorderedDrafts = reorderedItems.map((item) => ({
        ...item.draft,
        updatedAt: now,
      }));
      onMarketStatesChange(reorderedDrafts);
    },
    [onMarketStatesChange]
  );

  // Open edit modal for existing draft
  const handleEdit = useCallback((draftId: string) => {
    const draft = marketStates.find((d) => d.id === draftId);
    if (draft) {
      setEditingDraft(draft);
      setIsEditModalOpen(true);
    }
  }, [marketStates]);

  // Open delete confirmation for existing draft
  const handleDelete = useCallback((draftId: string) => {
    const draft = marketStates.find((d) => d.id === draftId);
    if (draft) {
      setDeletingDraft(draft);
    }
  }, [marketStates]);

  // Open add modal (no draft to edit)
  const handleAdd = useCallback(() => {
    setEditingDraft(undefined);
    setIsEditModalOpen(true);
  }, []);

  // Handle save from edit modal (add or update)
  const handleSaveFromModal = useCallback(
    (savedDraft: MarketStateDraft) => {
      const existingIndex = marketStates.findIndex((d) => d.id === savedDraft.id);

      if (existingIndex >= 0) {
        // Update existing draft
        const updatedStates = [...marketStates];
        updatedStates[existingIndex] = savedDraft;
        onMarketStatesChange(updatedStates);
      } else {
        // Add new draft
        onMarketStatesChange([...marketStates, savedDraft]);
      }

      setIsEditModalOpen(false);
      setEditingDraft(undefined);
    },
    [marketStates, onMarketStatesChange]
  );

  // Handle close of edit modal
  const handleEditModalClose = useCallback(() => {
    setIsEditModalOpen(false);
    setEditingDraft(undefined);
  }, []);

  // Handle confirmed delete
  const handleConfirmDelete = useCallback(() => {
    if (deletingDraft) {
      const filteredStates = marketStates.filter((d) => d.id !== deletingDraft.id);
      onMarketStatesChange(filteredStates);
    }
    setDeletingDraft(null);
  }, [deletingDraft, marketStates, onMarketStatesChange]);

  // Handle close of delete modal
  const handleDeleteModalClose = useCallback(() => {
    setDeletingDraft(null);
  }, []);

  // =============================================================================
  // Render
  // =============================================================================

  // Empty state
  if (marketStates.length === 0) {
    return (
      <>
        <EmptyState
          icon={<LineChart size={24} />}
          title="No market snapshots"
          description="Add snapshots to capture market conditions for your instruments"
          action={
            <Button
              color="primary"
              variant="flat"
              startContent={<Plus size={16} />}
              onPress={handleAdd}
            >
              Add Snapshot
            </Button>
          }
        />

        {/* Edit Modal */}
        <MarketSnapshotEditModal
          isOpen={isEditModalOpen}
          onClose={handleEditModalClose}
          editingDraft={editingDraft}
          existingDrafts={marketStates}
          onSave={handleSaveFromModal}
        />
      </>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {/* Sortable list of snapshots */}
        <SortableList
          items={sortableItems}
          onReorder={handleReorder}
          className="space-y-3"
          renderItem={(item, dragHandleProps) => (
            <SnapshotRowRenderer
              item={item}
              dragHandleProps={dragHandleProps}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          )}
        />

        {/* Add button at bottom */}
        <div className="flex justify-center pt-2">
          <Button
            variant="flat"
            startContent={<Plus size={16} />}
            onPress={handleAdd}
          >
            Add Snapshot
          </Button>
        </div>
      </div>

      {/* Edit Modal */}
      <MarketSnapshotEditModal
        isOpen={isEditModalOpen}
        onClose={handleEditModalClose}
        editingDraft={editingDraft}
        existingDrafts={marketStates}
        onSave={handleSaveFromModal}
      />

      {/* Delete Confirmation Modal */}
      <DeleteSnapshotConfirmationModal
        isOpen={!!deletingDraft}
        snapshot={deletingDraft}
        onClose={handleDeleteModalClose}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default MarketSnapshotsInput;
